import { UserMemberStatusDto } from '@act/shared/data-transfer-objects';
import DayBreaker from './day-breaker';
import { membersAppSelectors } from 'redux/slices/memberSlice';
import { EventItem } from 'redux/slices/feedSlice';
import { memberStatusChangeRequestAppSelectors } from 'redux/slices/memberStatusChangeRequestSlice';
import { timeFormatter } from '@act/shared/utilities';
import { DateTime } from 'luxon';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import {
  MessageOutlined,
  Update,
  PhoneOutlined,
  HomeWorkOutlined,
  ManageAccountsOutlined,
  ViewDayOutlined,
  Dangerous,
  School,
  PauseCircleOutlined,
  PersonAdd,
} from '@mui/icons-material';

import './member-status-event.scss';
import { Button } from '@act/storybook';
import {
  Box,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Permission } from '@act/shared/authorization';
import useConfirmationAlertDialog from 'react-components/dialog-forms/hooks/use-confirmation-alert-dialog';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useApiCall } from 'redux/utils/api-hook';
import { membersRequests } from 'redux/requests/members.request';
import { useToast } from 'react-components/app/ReusableToast';

interface MemberStatusEventProps {
  userMemberStatus: UserMemberStatusDto;
  scrollToEventFeedItem: (id: string | number) => void;
  showJumpButton?: boolean;
  previousItem?: EventItem;
}

interface DisplayProps {
  // 'message' | 'call' | 'visit' | 'statusChangeRequest'
  type: string;
  name: string;
  background: 'default' | 'success';
  icon: JSX.Element | string;
}

const MemberStatusEvent = ({
  userMemberStatus,
  previousItem,
  scrollToEventFeedItem,
  showJumpButton = false,
}: MemberStatusEventProps) => {
  const theme = useTheme();
  const previousTimestamp = previousItem ? previousItem.timestamp : 0;
  const [additionalMenuAnchor, setAdditionalMenuAnchor] =
    useState<null | HTMLElement>(null);
  const isManager = membersAppSelectors.useHasPermissionOnMember(
    userMemberStatus.memberId,
    Permission.CAN_AUDIT_MEMBER_STATUS_CHANGE_REQUESTS
  );
  const openConfirmationAlertDialog = useConfirmationAlertDialog();
  const memberStatusChangeRequest =
    memberStatusChangeRequestAppSelectors.useSelectMemberStatusChangeRequestById(
      userMemberStatus.memberStatusChangeRequestId
    );

  const openAdditionalMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAdditionalMenuAnchor(event.currentTarget);
  };
  const [
    dismissMemberStatusChangeResult,
    _isDismissing,
    dismissMemberStatusChange,
    dismissError,
  ] = useApiCall(membersRequests.dismissMemberStatusChange);
  const closeAdditionalMenu = () => {
    setAdditionalMenuAnchor(null);
  };
  const toast = useToast();
  const memberStatus = membersAppSelectors.useSelectMemberStatusById(
    userMemberStatus.memberStatusId
  );
  const open = Boolean(additionalMenuAnchor);

  if (!memberStatus) {
    return null;
  }

  const displayProps: DisplayProps = {
    name: memberStatus.name,
    type: null,
    background: 'default',
    icon: <MessageOutlined />,
  };

  if (userMemberStatus.externalItemType) {
    switch (userMemberStatus.externalItemType) {
      case 'call':
        displayProps.type = 'Call';
        displayProps.icon = <PhoneOutlined />;
        break;
      case 'message':
        displayProps.type = 'Message';
        displayProps.icon = <MessageOutlined />;
        break;
      case 'visit':
        displayProps.type = 'Visit';
        displayProps.icon = <HomeWorkOutlined />;
        break;
      default:
        break;
    }
  }

  if (userMemberStatus.memberStatusChangeRequestId) {
    displayProps.type = 'Request';
    displayProps.icon = <ManageAccountsOutlined />;
  }

  if (memberStatus.name === 'Queued') {
    displayProps.icon = <Update />;
  }

  if (memberStatus.name === 'New') {
    displayProps.type = null;
    displayProps.icon = <PersonAdd />;
  }

  if (['Re-Engaged', 'Engaged', 'Newly Engaged'].includes(memberStatus.name)) {
    displayProps.background = 'success';
  }

  switch (memberStatus.key) {
    case 'refused-services':
    case 'no-contact':
      displayProps.icon = <Dangerous />;
      break;
    case 'graduated':
      displayProps.icon = <School />;
      break;
    case 'paused':
      displayProps.icon = <PauseCircleOutlined />;
  }

  const time = timeFormatter.getDateString({
    date: DateTime.fromMillis(userMemberStatus.effectiveAt),
    // Hide the time if the status is queued
    includeTime: !memberStatus.key.includes('queued'),
  });

  let jumpButton = null;
  const externalItemId =
    userMemberStatus.externalItemId ||
    userMemberStatus.memberStatusChangeRequestId;

  if (showJumpButton && externalItemId) {
    jumpButton = (
      <Button
        onClick={() =>
          scrollToEventFeedItem(
            `${userMemberStatus.externalItemType}-${userMemberStatus.externalItemId}`
          )
        }
        variant="text"
      >
        <ViewDayOutlined />
      </Button>
    );
  }

  const deleteStatus =
    ['unable-to-newly-engage', 'unable-to-re-engage'].includes(
      memberStatus.key
    ) && !memberStatusChangeRequest;

  const handleDeleteStatus = async () => {
    closeAdditionalMenu();
    const shouldDelete = await openConfirmationAlertDialog({
      title: 'Warning',
      message: 'Are you sure you want to delete this status?',
      confirmationText: 'Yes, delete now',
      icon: <ErrorOutlineIcon />,
      iconColor: 'error',
      iconBorderColor: 'errorContainer',
      avatarCaption: 'Are you sure?',
      confirmButtonColor: 'error',
    });
    if (shouldDelete) {
      dismissMemberStatusChange({
        params: { id: userMemberStatus.memberStatusChangeRequestId },
      });
    }
  };

  useEffect(() => {
    if (dismissMemberStatusChangeResult) {
      toast('Status dismissed successfully', 'success');
    }
    if (dismissError) {
      const errorMessage =
        dismissError.userMessage ||
        dismissError.message ||
        'An error occurred while dismissing the status';
      toast(errorMessage, 'error');
    }
  }, [dismissError, dismissMemberStatusChangeResult]);

  return (
    <>
      <DayBreaker
        time={userMemberStatus.effectiveAt}
        previousTime={previousTimestamp}
      ></DayBreaker>
      <Box
        className={`status-event ${displayProps.background}`}
        sx={{
          display: 'flex',
          fontSize: '12px',
          fontWeight: '500',
          background:
            displayProps.background === 'success'
              ? theme.palette.successContainer.main
              : theme.palette.navyShades?.[50],
          border: '1px solid #DCE4E8',
          padding: '8px 24px',
          borderRadius: '6px',
          margin: '0',
        }}
      >
        <div className="description">
          {displayProps.icon}
          <p>
            {displayProps.name}{' '}
            {displayProps.type ? ` | ${displayProps.type}` : ''}
          </p>
        </div>
        <div className="meta">
          <p>{time}</p>
          {jumpButton}
          {deleteStatus && isManager && (
            <IconButton onClick={openAdditionalMenu}>
              <MoreVertIcon />
            </IconButton>
          )}
          <Menu
            anchorEl={additionalMenuAnchor}
            open={open}
            onClose={closeAdditionalMenu}
          >
            <MenuItem>
              <ListItemText onClick={handleDeleteStatus}>
                Dismiss Status
              </ListItemText>
            </MenuItem>
          </Menu>
        </div>
      </Box>
    </>
  );
};

export default MemberStatusEvent;
